import { DimensionDefinition, ModuleDefinition } from "@reporting/rp-types/definition";
import { FilterSectionConfig, FilterSectionType, VisualizationType } from "../types";
import { getPeriodDimensions, isExtensionContext, isDataModule, getModuleSpecificDimensions } from "../common";
import {
	AxisSection, ChartSection, DimensionSection, IndicatorSection, PeriodSection,
	VisualizationSection, StatementSection, defaultVisualizations
} from "../components";
import { msg } from "@omegagrid/localize";
import { ContextData, MultiContextData } from "@reporting/rp-types/analytics";
import { Parameter, Resource } from "@reporting/rp-types/ext";
import { dom } from "@omegagrid/core";
import { TreeAdapter, TreeOptions, TreeSource } from "@omegagrid/tree";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { AnalyticsApplication, CustomSection } from "..";
import rp from "@reporting/rp-app-host";
import type { Filter, MultiDimensionSection } from "../components";
import { FilterSectionEvent } from "../components/filters/sectionBase";
import { getModuleDefinitions } from "../actions/model";

export class CustomParameterTreeAdapter extends TreeAdapter {

	private data: TreeSource;

	constructor(private parameter: Parameter) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.data) return this.data;

		try {
			this.data = await rp.ms('extensions').get<TreeSource>(`parameterContent/${this.parameter.idz}`);
			return this.data;
		} catch (error) {
			return [
				{k: null, v: error, icon: {icon: "hexagon-exclamation", color: 'red'}}
			];
		}
	}

	getOptions(): Partial<TreeOptions> {
		const multiselect = /^table/.test(this.parameter.data_type);
		return {
			selectMode: multiselect ? 'multiple' : 'single',
			autoClose: !multiselect,
			allowEmpty: true
		};
	}

}

const getCustomVisualizationName = (resource: Resource) => {
	if (resource.description) return resource.description;
	let name = resource.name;
	const dotIndex = name.lastIndexOf('.');
	if (dotIndex !== -1) name = name.substring(0, dotIndex);
	const parts = name.split('/');
	return parts[parts.length - 1];
}

let _defaultSections: Map<FilterSectionType, FilterSectionConfig> | null = null;
export const getDefaultSections = () => {
	if (_defaultSections) return _defaultSections;

	_defaultSections = new Map<FilterSectionType, FilterSectionConfig>([
		['visualizations', {
			type: 'visualizations',
			title: msg('Visualizations'),
			icon: 'chart-tree-map',
			ordNum: 0,
			factory: async opts => {
				const showDetails = rp.userProvider.hasRight('custom.define');
				const resources = (await rp.app<AnalyticsApplication>('analytics'))
					.resources.get(opts.module.module as ModuleCode) || [];

				const customVisualizations = resources.map(v => ({
					id: `custom-${v.id_custom_resource}`,
					name: getCustomVisualizationName(v),
					type: 'custom' as VisualizationType,
					icon: 'puzzle-piece',
					resource: showDetails ? v : null
				}));

				return dom.createElement<VisualizationSection>('rp-fsection-visualization', {
					standardItems: isDataModule(opts.module.module as ModuleCode) ? defaultVisualizations : [],
					customItems: customVisualizations,
				});
			}
		}],
		['statement', {
			type: 'statement',
			title: msg('Statement'),
			icon: 'suitcase',
			ordNum: 0,
			fields: ['id_statement', 'rows'],
			factory: opts => dom.createElement<StatementSection>('rp-fsection-statement', {
				module: opts.module
			})
		}],
		['period', {
			type: 'period',
			title: msg('Period'),
			icon: 'calendar-days',
			ordNum: 0,
			fields: ['period', 'static_period', 'dynamic_period'],
			// module may be missing (e.g. a multi context module without its
			// own definition) - getPeriodDimensions falls back to the default
			factory: opts => dom.createElement<PeriodSection>('rp-fsection-period', {
				periods: getPeriodDimensions(opts.module?.module as ModuleCode),
				module: opts.module
			})
		}],
		['dimensions', {
			type: 'dimensions',
			title: msg('Dimensions'),
			icon: 'hexagon',
			ordNum: 0,
			fields: [/^dim_?\d+$/],
			factory: opts => dom.createElement<DimensionSection>('rp-fsection-dimensions', {
				module: opts.module,
			})
		}],
		['multidimensions', {
			type: 'multidimensions',
			title: msg('Dimensions'),
			icon: 'hexagon',
			ordNum: 0,
			factory: opts => dom.createElement<MultiDimensionSection>('rp-fsection-multidimensions')
		}],
		['indicators', {
			type: 'indicators',
			title: msg('Indicators'),
			icon: 'display-chart-up',
			ordNum: 0,
			fields: ['indicators'],
			factory: opts => dom.createElement<IndicatorSection>('rp-fsection-indicators', {
				module: opts.module,
				dimensionDefs: [
					...opts.module.dimensions,
					...getModuleSpecificDimensions(opts.module.module as ModuleCode),
					{dim_num: 'PR', description: `${msg('Period')} (${msg('relative')})`},
					...getPeriodDimensions().filter(d => d.dim_num !== 'PX')
				]
			})
		}],
		['axes', {
			type: 'axes',
			title: msg('Axes'),
			icon: 'xy-axes',
			ordNum: 0,
			factory: opts => dom.createElement<AxisSection>('rp-fsection-axes', {
				module: opts.module,
				periods: [
					{dim_num: 'P', description: msg('Period')},
					{dim_num: 'PC', description: `${msg('Period')} (${msg('common')})`},
				]//getPeriodDimensions(),
			})
		}],
		['chart', {
			type: 'chart',
			title: msg('Chart'),
			icon: 'chart-line',
			ordNum: 0,
			factory: opts => dom.createElement<ChartSection>('rp-fsection-chart', {
				module: opts.module
			})
		}],
	]);

	return _defaultSections;
}

export interface FilterAdapter {
	getSections(context?: ContextData | ModuleDefinition): Promise<FilterSectionConfig[]> | FilterSectionConfig[];
	attached(component: Filter) : void;
}

export class MultiFilterAdapter implements FilterAdapter {

	get modules() { return [...getModuleDefinitions().values()] as ModuleDefinition[] }

	attached(component: Filter) { }

	async getSections(context?: MultiContextData) : Promise<FilterSectionConfig[]> {
		if (!context) return [];
		const sections: FilterSectionConfig[] = [];
		const periodSection = {
			module: this.modules.find(m => m.module == context.module),
			contextData: context,
			...getDefaultSections().get('period')
		};
		sections.push(periodSection);
		sections.push({
			...getDefaultSections().get('multidimensions'),
			factory: () => dom.createElement<MultiDimensionSection>('rp-fsection-multidimensions', {
				modules: this.modules.filter(m => isDataModule(m.module as ModuleCode))
			})
		});
		return sections;
	}
}

export class DefaultFilterAdapter implements FilterAdapter {

	constructor(readonly module: ModuleDefinition) { }

	attached(component: Filter) {
		component.addEventListener('filter.section.change', async (e: FilterSectionEvent) => {
			if (e.contextKey == 'view') {
				// different views can have different sections so we need to update the section configs
				component.reload();
			} else if (e.contextKey == 'filters' && e.sectionType == 'period') {
				// fin dimensions depend on selected fiscal years
				const dimSection = Array.from(component.sections).find(s => s.config.type == 'dimensions').sectionComponent as DimensionSection;
				dimSection?.resetItems();
			}
		});
	}

	private getStandardSection(context: ContextData) : FilterSectionConfig[] {
		const sections: FilterSectionConfig[] = [];

		if (context?.module == 'F') {
			sections.push(getDefaultSections().get('statement'));
		}

		sections.push(getDefaultSections().get('period'));
		sections.push(getDefaultSections().get('dimensions'));
		sections.push(getDefaultSections().get('indicators'));
		sections.push(getDefaultSections().get('axes'));

		// const type = visualizationMap.get(context.view)?.type;
		// if (type == 'chart') {
		// 	sections.push(getDefaultSections().get('chart'));
		// }

		return sections;
	}

	private async getCustomSections(context: ContextData) : Promise<FilterSectionConfig[]> {
		const id = parseInt(context.view.split('-')[1]);
		if (!id) return [];

		const [parameters, filterResources] = await Promise.all([
			rp.ms('extensions').get<Parameter[]>(`resourceParameters/${id}`, {
				fields: [
					'id_thread', 'accounting', 'name', 'description', 'data_type', 'ord_num','id_thread_list',
					'content', 'default_value', 'default_value_sql', 'global', 'options', 'value', 'idz'
				]
			}),
			rp.ms('extensions').get<Resource[]>(`filterResources/${id}`)
		]);

		const parameterSet = new Set(parameters);
		const sections: FilterSectionConfig[] = [];

		if (context.module != 'C') {
			// match default sections required by custom view
			getDefaultSections().forEach(sect => {
				if (!sect.fields) return;
				for (const field of sect.fields) {
					for (const p of parameters) {
						if (p.id_thread_list && p.content) continue; // parameters with custom list query

						const paramName = p.name.toLowerCase();
						if ((typeof field === 'string' && field === paramName) || (field instanceof RegExp && field.test(paramName))) {
							sections.push(sect);
							parameterSet.delete(p);
							return;
						}
					}
				}
			});
		}

		// remaining parameters go to custom sections
		if (parameterSet.size > 0) {
			const customSections = new Map<string, FilterSectionConfig>();
			parameterSet.forEach(p => {
				if (!p.description) return;
				const sectionName = p.options?.section as string || 'Parameters';
				if (!customSections.has(sectionName)) {
					customSections.set(sectionName, {
						type: 'custom',
						title: sectionName,
						parameters: [],
						icon: 'puzzle-piece',
						factory: (opts) => dom.createElement<CustomSection>('rp-fsection-custom', {
							module: opts.module,
							parameters: opts.config.parameters,
							customScripts: filterResources,
							createTreeAdapter: (param: Parameter) => new CustomParameterTreeAdapter(param)
						})
					});
				}
				customSections.get(sectionName)?.parameters?.push(p);
			});
			sections.push(...customSections.values());
		}

		return sections;
	}

	async getSections(context?: ContextData): Promise<FilterSectionConfig[]> {
		const sections: FilterSectionConfig[] = [];
		sections.push(getDefaultSections().get('visualizations'));

		if (isExtensionContext(context || {})) {
			sections.push(...await this.getCustomSections(context));
		} else if (context) {
			sections.push(...this.getStandardSection(context));
		}

		return sections;
	}

}
